import { useNotification } from '@/ui/Notification'
import { QueryKey, useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { trackException } from '@/helpers/telemetry/appInsights.ts'
import { SeverityLevel } from '@microsoft/applicationinsights-web'

type NetworkMode = 'online' | 'always' | 'offlineFirst'

const DEFAULT_NETWORK_MODE: NetworkMode = 'always'

interface MutationOptimisticChanges<TQueryData, TData> {
    queryKey?: QueryKey
    optimisticChangesCallback?: (requestBody: TData, previousData: NoInfer<TQueryData> | undefined) => any
    alertOnError?: boolean
}

export const useAppMutation = <
    TMutationFnData = unknown,
    TError = unknown,
    TData = void,
    TQueryData = unknown,
    TContext = unknown,
>({
    queryKey,
    alertOnError = false,
    optimisticChangesCallback,
    onError: callerOnError,
    ...options
}: UseMutationOptions<TMutationFnData, TError, TData, TContext> &
    MutationOptimisticChanges<TQueryData, TData>): UseMutationResult<TMutationFnData, TError, TData, TContext> => {
    const queryClient = useQueryClient()
    const { showNotification } = useNotification()

    return useMutation({
        networkMode: DEFAULT_NETWORK_MODE,
        onMutate: async (requestBody: TData) => {
            if (!queryKey || !optimisticChangesCallback) {
                return {} as TContext
            }

            await queryClient.cancelQueries({ queryKey })

            const [[_, previousData]] = queryClient.getQueriesData({ queryKey })
            queryClient.setQueryData<TQueryData>(queryKey, (oldData) => optimisticChangesCallback(requestBody, oldData))

            return { previousData, requestBody } as TContext
        },
        onError: (err: TError, variables: TData, context: any) => {
            if (alertOnError) {
                showNotification({
                    type: 'error',
                    title: 'Changes not saved',
                    message:
                        "We're sorry, something unexpected has happened. Please check your work, as not all changes may have been saved.",
                    manualDismiss: true,
                })
            }

            // Log to App Insights. Include id and reference if there is one.
            let message = 'Mutation failed'
            if (variables && typeof variables === 'object' && 'id' in variables && 'reference' in variables) {
                const v = variables as { id: string; reference: string }
                message = `Mutation failed id=${v.id} reference=${v.reference}`
            }
            trackException(message, { severityLevel: SeverityLevel.Error, properties: [err, variables] })

            if (queryKey) {
                queryClient.setQueryData(queryKey, () => context.previousData)
            }

            callerOnError?.(err, variables, context)
        },
        ...options,
    })
}
